import type { SurfaceOrientation } from "@/types/bien";

/**
 * Répéteur « surface + orientation » — logique de patch partagée.
 *
 * Chantier fiche en onglets (2026-08-08) : la spec impose un répéteur unique
 * chambres / terrasses sur la fiche VENTE **et** la fiche LOCATION. Les deux
 * fiches doivent donc produire exactement le même patch, miroirs compris.
 *
 * ── Bug corrigé au passage (« trois colonnes pour la surface des chambres ») ──
 * Il existe trois colonnes pour la même information :
 *   - `surfacesChambres` (string[])  → LA colonne lue par le mapper Immoweb ;
 *   - `chambresDetail`   (objets)    → le répéteur (surface + orientation) ;
 *   - `surfaceChambre1`              → morte.
 * Le répéteur de la fiche vente n'alimentait QUE `chambresDetail` : les surfaces
 * d'un bien en vente n'arrivaient donc jamais sur Immoweb. Le miroir est
 * désormais posé ici, à la source, donc pour les deux fiches à la fois.
 */

/** Lit une valeur de répéteur en se protégeant d'un sessionStorage périmé. */
export function readRepeaterRows(value: unknown): SurfaceOrientation[] {
  return Array.isArray(value) ? (value as SurfaceOrientation[]) : [];
}

/**
 * Lignes à AFFICHER dans le répéteur, en absorbant les colonnes historiques.
 *
 * Indispensable côté location : les ~1560 biens issus de l'import portent leurs
 * surfaces dans `surfacesChambres` / `surfaceTerrasse` (l'ancienne saisie à plat)
 * et n'ont jamais eu de `chambresDetail` / `terrasses`. Sans cette reprise, la
 * bascule vers le répéteur afficherait un bloc vide sur tout le parc — ce qui se
 * lit comme une perte de données.
 *
 * Lecture seule : rien n'est réécrit en base tant que l'utilisateur ne modifie
 * pas une ligne. Au premier changement, `buildRepeaterPatch` réaligne les deux
 * colonnes d'un coup.
 */
export function hydrateRepeaterRows(
  key: "chambresDetail" | "terrasses",
  source: { chambresDetail?: unknown; surfacesChambres?: unknown; terrasses?: unknown; surfaceTerrasse?: unknown },
): SurfaceOrientation[] {
  if (key === "terrasses") {
    const rows = readRepeaterRows(source.terrasses);
    if (rows.length > 0) return rows;
    const legacy = typeof source.surfaceTerrasse === "string" ? source.surfaceTerrasse.trim() : "";
    return legacy ? [{ surface: legacy, orientation: "" }] : [];
  }
  const rows = readRepeaterRows(source.chambresDetail);
  if (rows.length > 0) return rows;
  const legacy = Array.isArray(source.surfacesChambres) ? (source.surfacesChambres as string[]) : [];
  return legacy.map((surface) => ({ surface: surface ?? "", orientation: "" }));
}

/**
 * Construit le patch à appliquer au form state quand les lignes changent.
 *
 * - `chambresDetail` → synchronise `nbChambres` (compte des lignes, **y compris
 *   0** : borner à `length >= 1` laissait un compte périmé quand on supprimait
 *   toutes les lignes) et le miroir `surfacesChambres` lu par Immoweb.
 * - `terrasses` → synchronise le miroir `surfaceTerrasse` (1re terrasse), déjà
 *   attendu par le mapper.
 */
export function buildRepeaterPatch(
  key: "chambresDetail" | "terrasses",
  next: SurfaceOrientation[],
): Record<string, unknown> {
  if (key === "terrasses") {
    return { terrasses: next, surfaceTerrasse: next[0]?.surface ?? "" };
  }
  return {
    chambresDetail: next,
    nbChambres: String(next.length),
    // Miroir Immoweb : une entrée par chambre, dans le même ordre.
    surfacesChambres: next.map((r) => r.surface ?? ""),
  };
}
